import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';

import { FormLabel } from './shared';
import { CardModel } from './shared/card-model';
import { FocusedElement } from './shared/focused-element';
import { DxSelectBoxModule } from 'devextreme-angular/ui/select-box';
import { DxTextBoxModule } from 'devextreme-angular/ui/text-box';
import { DxiValidationRuleModule } from 'devextreme-angular/ui/nested';
import { DxValidatorModule } from 'devextreme-angular/ui/validator';
import { DxRadioGroupModule } from 'devextreme-angular/ui/radio-group';
import { CardComponent } from './card/card.component';
import { NgStyle } from '@angular/common';

@Component({
    selector: 'pxw-interactive-paycard',
    templateUrl: 'interactive-paycard.component.html',
    styleUrls: ['./interactive-paycard.component.scss'],
    imports: [
        CardComponent,
        NgStyle,
        DxRadioGroupModule,
        DxValidatorModule,
        DxiValidationRuleModule,
        DxTextBoxModule,
        DxSelectBoxModule,
    ]
})
export class InteractivePaycardComponent implements OnInit {
  @Input() showSubmitButton = true;
  @Input() showCvvInput = true;
  @Input() showCreditCard = true;
  @Input() showDocumentSelector = false;
  @Input() cardModel: CardModel = {
    cardNumber: '',
    cardName: '',
    expirationMonth: '',
    expirationYear: '',
    cvv: '',
    dni: '',
    creditCard: undefined,
    documentType: undefined,
  };

  cardNumberMaxLength = 19;
  minCardYear = new Date().getFullYear();
  displayedCardNumber = this.cardModel.cardNumber; // The displayedCardNumber can be masked, the cardModel.cardNumber contains the real data

  cardNumberId = 'cardNumberId';
  cardNameId = 'cardNameId';
  cardDniId = 'cardDniId';
  cardDocumentTypeId = 'cardDocumentTypeId';
  monthSelect = 'monthSelect';
  yearSelectId = 'yearSelectId';
  cardCvvId = 'cardCvvId';

  focusedElement: FocusedElement | undefined;
  cardNumberFormatArray: string[] = [];

  expirationMonths: string[] = [];
  expirationYears: string[] = [];

  cardNumberFormat = '#### #### #### ####';
  cardNumberMask = '#### **** **** ####';
  formLabels: FormLabel = {
    cardNumber: 'Número de Tarjeta',
    cardHolderName: 'Titular de la Tarjeta',
    cardHolderDNI: 'Documento',
    documentType: 'Tipo de documento',
    expirationDate: 'Fecha de Expiración',
    expirationMonth: 'Mes',
    expirationYear: 'Año',
    submitButton: 'Enviar',
    cvv: 'CVV',
    typeCard: 'Tipo de tarjeta',
  };

  cardTypes = [
    { label: 'Tarjeta de débito', value: 0 },
    { label: 'Tarjeta de crédito', value: 1 },
  ];

  documentTypes = [
    { label: 'DNI', value: 'dni' },
    { label: 'CUIT', value: 'cuit' },
    { label: 'CUIL', value: 'cuil' },
  ];

  selectedDocumentType = 'dni';

  @ViewChild('cardNumberInput', { static: false })
  cardNumberInputViewChild!: ElementRef;

  ngOnInit() {
    if (new RegExp('[^# ]').test(this.cardNumberFormat)) {
      throw new Error(
        'The card number format must contain only "#" and " " characters! Check the "cardNumberFormat" input parameter!',
      );
    }
    if (new RegExp('[^# *]').test(this.cardNumberMask)) {
      throw new Error(
        'The card number mask must contain only "#", "*" and " " characters! Check the "cardNumberMask" input parameter!',
      );
    }
    if (this.cardNumberMask.length !== this.cardNumberFormat.length) {
      throw new Error(
        'The card number mask and the card number format must have the same length! \
      Check the "cardNumberFormat" and the "cardNumberMask" input parameters!',
      );
    }
    this.cardNumberMaxLength = this.cardNumberFormat.length;
    this.cardNumberFormatArray = this.cardNumberFormat.split('');
    this.initVariables();

    // Initialize documentType based on default or existing value
    if (!this.cardModel.documentType) {
      this.cardModel.documentType = 'DNI';
    }
  }

  private initVariables() {
    this.initExpirationMonths();
    this.initExpirationYears();
  }

  private initExpirationMonths() {
    const months = [];
    const monthPositions = new Array(12);

    for (const [index] of monthPositions.entries()) {
      const month = index + 1 < 10 ? '0' + (index + 1) : index + 1;
      months.push(month.toString());
    }

    this.expirationMonths = months;
  }

  private initExpirationYears() {
    const years = [];
    const yearsPosition = new Array(12);

    for (const [index] of yearsPosition.entries()) {
      const year = this.minCardYear + index;
      years.push(year.toString());
    }

    this.expirationYears = years;
  }

  onCardNumberChange($event: any): void {
    const target = $event.event.currentTarget;

    let cursorPosStart = target.selectionStart;
    let cursorPosEnd = target.selectionEnd;
    let processedCardNumber: string = target.value;
    const newValues: string[] = [];
    const letterRegex = new RegExp('[^0-9]');
    const isCursorAtTheEnd = cursorPosEnd === processedCardNumber.length;
    const cardNumWithoutSpaceAsArray = processedCardNumber.replace(/ /g, '').split('');
    this.cardNumberFormatArray.forEach(format => {
      if (cardNumWithoutSpaceAsArray.length > 0) {
        if (format === '#') {
          let isNumber: boolean;
          let character: string;
          do {
            character = cardNumWithoutSpaceAsArray.shift() || '';
            isNumber = !letterRegex.test(character);
            if (isNumber) {
              newValues.push(character);
            } else {
              // don't move the cursor, if a letter is removed
              cursorPosEnd--;
              cursorPosStart--;
            }
          } while (!isNumber && character !== undefined); // find the next number
        } else if (format === ' ') {
          newValues.push(' ');
        }
      }
    });
    processedCardNumber = newValues.join('').trim();
    this.displayedCardNumber = processedCardNumber;
    this.cardModel.cardNumber = processedCardNumber;
    target.value = processedCardNumber; // The value in event has to be updated, otherwise the letter remains in the <input>
    if (!isCursorAtTheEnd) {
      // The cursor position has to be corrected because of the newly created string
      target.selectionEnd = cursorPosEnd;
      target.selectionStart = cursorPosStart;
    }
  }

  onCvvChange(event: any): void {
    const newValue = event.value || '';
    // Limitar a solo números
    const cleanValue = newValue.replace(/[^0-9]/g, '');
    this.cardModel.cvv = cleanValue;

    // Actualizar el valor en el input si es diferente (solo si contiene caracteres no numéricos)
    if (event.component && cleanValue !== newValue) {
      event.component.option('value', cleanValue);
    }
  }

  onCardNumberFocus(): void {
    this.unMaskCardNumber();
    this.focusedElement = FocusedElement.CardNumber;
  }

  onCardNameFocus(): void {
    this.focusedElement = FocusedElement.CardName;
  }

  onDateFocus(): void {
    this.focusedElement = FocusedElement.ExpirationDate;
  }

  onCvvFocus(): void {
    this.focusedElement = FocusedElement.CVV;
  }

  onBlur(): void {
    this.focusedElement = undefined;
  }

  onCardNumberBlur(): void {
    this.maskCardNumber();
    this.onBlur();
  }

  onCvvBlur(): void {
    this.onBlur();
  }

  onCardNameKeyPress($event: any): boolean {
    const keyCode = $event.event.keyCode;

    return (keyCode >= 65 && keyCode <= 90) || (keyCode >= 97 && keyCode <= 122) || keyCode === 32;
  }

  onYearChange(): void {
    if (this.cardModel.expirationYear === this.minCardYear.toString()) {
      this.cardModel.expirationMonth = '';
    }
  }

  minCardMonth(): number {
    if (this.cardModel.expirationYear === this.minCardYear.toString()) {
      return new Date().getMonth() + 1;
    } else {
      return 1;
    }
  }

  generateMonthValue(index: number): string {
    return index < 10 ? `0${index}` : index.toString();
  }

  private maskCardNumber(): void {
    this.cardModel.cardNumber = this.displayedCardNumber;
    const arr = (this.displayedCardNumber || '').split('');
    arr.forEach((element, index) => {
      if (this.cardNumberMask[index] === '*') {
        arr[index] = '*';
      }
    });
    this.displayedCardNumber = arr.join('');
  }

  private unMaskCardNumber(): void {
    this.displayedCardNumber = this.cardModel.cardNumber;
  }

  getDocumentMask(): string {
    if (!this.showDocumentSelector) {
      return '00.000.000';
    }

    switch (this.selectedDocumentType) {
      case 'dni':
        return '00.000.000';
      case 'cuit':
        return '00-00000000-0';
      case 'cuil':
        return '00-00000000-0';
      default:
        return '00.000.000';
    }
  }

  getDocumentValidationMessage(): string {
    if (!this.showDocumentSelector) {
      return 'El DNI es requerido';
    }

    switch (this.selectedDocumentType) {
      case 'dni':
        return 'El DNI es requerido';
      case 'cuit':
        return 'El CUIT es requerido';
      case 'cuil':
        return 'El CUIL es requerido';
      default:
        return 'El documento es requerido';
    }
  }

  onDocumentTypeChange(): void {
    // Clear the current document value when changing type
    this.cardModel.dni = '';

    // Update the documentType in cardModel
    switch (this.selectedDocumentType) {
      case 'dni':
        this.cardModel.documentType = 'DNI';
        break;
      case 'cuit':
        this.cardModel.documentType = 'CUIT';
        break;
      case 'cuil':
        this.cardModel.documentType = 'CUIL';
        break;
      default:
        this.cardModel.documentType = 'DNI';
    }
  }

  validateDocument = (params: any) => {
    const value = params.value;
    if (!value) return true; // Required validation will handle empty values

    const cleanValue = value.replace(/[.-]/g, '');

    switch (this.selectedDocumentType) {
      case 'dni':
        // DNI should be 7 or 8 digits
        return /^\d{7,8}$/.test(cleanValue);
      case 'cuit':
      case 'cuil':
        // CUIT/CUIL should be 11 digits
        if (!/^\d{11}$/.test(cleanValue)) {
          return false;
        }
        // Validate CUIT/CUIL checksum
        return this.validateCuitCuil(cleanValue);
      default:
        return true;
    }
  };

  private validateCuitCuil(cuitCuil: string): boolean {
    if (cuitCuil.length !== 11) return false;

    const multipliers = [5, 4, 3, 2, 7, 6, 5, 4, 3, 2];
    let sum = 0;

    for (let i = 0; i < 10; i++) {
      sum += parseInt(cuitCuil[i]) * multipliers[i];
    }

    const remainder = sum % 11;
    const checkDigit = remainder === 0 ? 0 : 11 - remainder;

    return checkDigit === parseInt(cuitCuil[10]);
  }

  getDocumentValidationErrorMessage(): string {
    switch (this.selectedDocumentType) {
      case 'dni':
        return 'El DNI debe tener entre 7 y 8 dígitos';
      case 'cuit':
        return 'El CUIT debe ser válido (11 dígitos con dígito verificador correcto)';
      case 'cuil':
        return 'El CUIL debe ser válido (11 dígitos con dígito verificador correcto)';
      default:
        return 'El documento no es válido';
    }
  }
}
